import React from 'react';
import PropTypes from 'prop-types';
import { isoDateToString } from '../../utils/formatUnits';

/**
 * Component to display the name and date metadata of an asset. It shows the created and modified dates, as well as the creator and last modifier of the asset. The component also includes buttons to view usage and edit the asset, which scroll to the respective sections when clicked.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.data - The asset data containing the name, createdBy, createdDate, modifiedBy, and modifiedDate.
 * @param {Array} props.users - An array of user objects to match the createdBy and modifiedBy IDs to user names.
 * @returns {JSX.Element|null} The rendered component or null if required data is missing.
 */
export function DateNameMetadata(props) {
  const generateName = user => {
    if (!user) {
      return 'N/A';
    }

    const { firstname, lastname, name } = user;

    if (firstname.trim() === '' || lastname.trim() === '') {
      return name;
    }

    return `${firstname} ${lastname}`;
  };

  const getUserFromUsers = (userId, users) => {
    if (!userId) {
      return false;
    }

    const userPosition = users.findIndex(user => user.user_id === userId);

    if (userPosition === -1) {
      return false;
    }

    return users[userPosition];
  };

  const generateChars = user => {
    const chars = [];

    if (user.firstname.trim().length === 0 && user.lastname.trim().length === 0) {
      chars[0] = user.name[0].toUpperCase();
    } else {
      if (user.firstname && user.firstname.length !== 0) {
        chars.push(user.firstname[0].toUpperCase());
      }

      if (user.lastname && user.lastname.length !== 0) {
        chars.push(user.lastname[0].toUpperCase());
      }
    }

    return chars;
  };

  const getEventInfo = (userId, users, date) => {
    const user = getUserFromUsers(userId, users);
    const chars = user ? generateChars(user) : '';
    const name = generateName(user);

    return {
      dateString: isoDateToString(date),
      avatarLetters: chars ? chars.join('') : '',
      name,
    };
  };

  const { data, users = [] } = props;

  if (!data || !users?.length || !data.createdDate) {
    return null;
  }

  const { modifiedBy = '', modifiedDate = '', createdBy, createdDate } = data;
  const {
    dateString: createdDateString,
    avatarLetters: creatorAvatarLetters,
    name: creatorName,
  } = getEventInfo(createdBy, users, createdDate);
  const {
    dateString: modifiedDateString,
    avatarLetters: modifiedAvatarLetter,
    name: modifierName,
  } = getEventInfo(modifiedBy, users, modifiedDate);

  return (
    <div data-test="metadata-container" className="flex items-center justify-center font-sans">
      <div className="w-full bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] p-4 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="space-y-8">
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold mb-4">
                Timeline
              </h3>
              <div className="relative pl-6 border-l border-gray-100 space-y-8">
                <div className="relative">
                  <div className="absolute -left-[29px] top-1 w-3 h-3 rounded-full bg-black border-4 border-white ring-1 ring-black" />
                  <p className="!mb-0 text-sm font-bold text-black leading-none">Modified Date</p>
                  <p
                    className="!mb-0 text-xs text-gray-400 mt-1"
                    data-test="metadata-modified-date"
                  >
                    {modifiedDateString}
                  </p>
                </div>
                <div className="relative">
                  <div className="absolute -left-[29px] top-1 w-3 h-3 rounded-full bg-gray-200 border-4 border-white" />
                  <p className="!mb-0 text-sm font-bold text-gray-400 leading-none">Created Date</p>
                  <p className="!mb-0 text-xs text-gray-300 mt-1" data-test="metadata-created-date">
                    {createdDateString}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold mb-4 ">
                Ownership
              </h3>
              <div className="space-y-6">
                <div className="flex items-center group ">
                  <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center text-white text-xs font-bold transition-transform group-hover:scale-110">
                    {creatorAvatarLetters}
                  </div>
                  <div className="ml-4">
                    <p className="!mb-0 text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                      Creator
                    </p>
                    <p className="!mb-0 text-sm font-bold text-black" data-test="metadata-creator">
                      {creatorName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center group ">
                  <div className="w-10 h-10 rounded-xl border border-gray-200 flex items-center justify-center text-black text-xs font-bold transition-transform group-hover:scale-110">
                    {modifiedAvatarLetter}
                  </div>
                  <div className="ml-4">
                    <p className="!mb-0 text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                      Last Modified
                    </p>
                    <p className="!mb-0 text-sm font-bold text-black" data-test="metadata-modifier">
                      {modifierName}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

DateNameMetadata.propTypes = {
  data: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
};
